import { useState } from "react";

import report from "./quality-data.json";

interface Figure {
  status: string;
  value: unknown;
  note: string;
}

interface Run {
  run: string;
  variant: string;
  scope: string;
  known_answers: { total: number; matched: number };
  populations: Record<string, number>;
  left_out_of_scope: Record<string, string>;
  dimensions: Record<string, Record<string, Figure>>;
  observability: {
    stage_seconds: Record<string, number>;
    queue: { peak_ready: number; ready: number } | null;
    provider: { spent_usd: string | null; calls: number | null; note: string };
    claims_by_lane: Record<string, number>;
    claims_by_grade: Record<string, number>;
  };
}

const RUNS = report.runs as unknown as Run[];
const POPULATIONS = ["resolved", "held", "conflicted", "unknown", "excluded", "failed"];

function shown(figure: Figure): string {
  if (figure.status === "not_measured_v1") return "Not measured in V1";
  if (figure.status === "not_applicable") return "Not applicable";
  if (figure.status === "not_measured") return "Not measured";
  const value = typeof figure.value === "object" ? JSON.stringify(figure.value) : String(figure.value);
  return figure.status === "measured_elsewhere" ? `${value} (measured once, for all runs)` : value;
}

function counts(values: Record<string, number>): string {
  return Object.entries(values)
    .map(([key, value]) => `${key}: ${value}`)
    .join(", ");
}

/** Each proof run's measures. Held, conflicted, unknown, excluded, and failed stay apart from success,
 * and what a run could not measure says so. Uncued and baseline runs are separate rows on purpose. */
export function QualityDashboardPage() {
  const [name, setName] = useState(RUNS[0]?.run ?? "");
  const run = RUNS.find((candidate) => candidate.run === name);
  return (
    <main>
      <h1>Quality dashboard</h1>
      <p>{report.scripted}</p>
      {report.benchmark && (
        <section>
          <h2>Benchmark</h2>
          <p>
            {report.benchmark.items} files, {report.benchmark.final_state}, at{" "}
            {report.benchmark.files_per_minute} files a minute. Duplicate jobs:{" "}
            {report.benchmark.duplicate_jobs}. Unexplained repeats:{" "}
            {report.benchmark.unexplained_repeats}. {report.benchmark.limits}
          </p>
          <p>Accounting: {counts(report.benchmark.accounting)}</p>
        </section>
      )}
      <label>
        Run{" "}
        <select value={name} onChange={(event) => setName(event.target.value)}>
          {RUNS.map((candidate) => (
            <option key={candidate.run} value={candidate.run}>
              {candidate.run}
            </option>
          ))}
        </select>
      </label>
      {run === undefined ? (
        <p>No proof runs are recorded yet.</p>
      ) : (
        <>
          <p>
            Scope: {run.scope}. Known answers matched: {run.known_answers.matched} of{" "}
            {run.known_answers.total}.
          </p>
          <h2>Populations</h2>
          <table>
            <thead>
              <tr>
                {POPULATIONS.map((population) => (
                  <th key={population}>{population}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {POPULATIONS.map((population) => (
                  <td key={population}>{run.populations[population] ?? 0}</td>
                ))}
              </tr>
            </tbody>
          </table>
          {Object.keys(run.left_out_of_scope).length > 0 && (
            <p>Left out of this run: {Object.keys(run.left_out_of_scope).join(", ")}</p>
          )}
          <h2>Performance</h2>
          <p>Stage seconds: {counts(run.observability.stage_seconds)}</p>
          <p>Peak ready queue: {run.observability.queue?.peak_ready ?? "not recorded"}</p>
          <p>
            Provider cost: ${run.observability.provider.spent_usd ?? "0"} over{" "}
            {run.observability.provider.calls ?? 0} calls. {run.observability.provider.note}
          </p>
          <p>Claims by lane: {counts(run.observability.claims_by_lane)}</p>
          <p>Claims by grade: {counts(run.observability.claims_by_grade)}</p>
          {Object.entries(run.dimensions).map(([section, figures]) => (
            <section key={section}>
              <h2>{section}</h2>
              <table>
                <thead>
                  <tr>
                    <th>Measure</th>
                    <th>Value</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(figures).map(([measure, figure]) => (
                    <tr key={measure}>
                      <td>{measure}</td>
                      <td>{shown(figure)}</td>
                      <td>{figure.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </>
      )}
    </main>
  );
}
